import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Download, Pencil, Trash2, Search, Users, Trophy, Award, Medal, DollarSign, CalendarRange, TrendingDown, Smartphone, Sparkles, Loader2, Building2, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import OnboardingLinkCard from "@/components/OnboardingLinkCard";
import OnboardingPendentesCard from "@/components/OnboardingPendentesCard";
import TempoRespostaOnboardingCard from "@/components/TempoRespostaOnboardingCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatCard from "@/components/StatCard";
import RankingReceita from "@/components/RankingReceita";
import MetasVendedor from "@/components/MetasVendedor";
import ComissoesCard from "@/components/ComissoesCard";
import PlanConfigCard from "@/components/PlanConfigCard";
import TeamGoalCard from "@/components/TeamGoalCard";
import ComparativoMensal from "@/components/ComparativoMensal";
import ProspectosCard from "@/components/ProspectosCard";
import RankingApps from "@/components/RankingApps";
import MetasProspeccao from "@/components/MetasProspeccao";
import MotivosFrequentesCard from "@/components/MotivosFrequentesCard";
import HeatmapLocalidade from "@/components/HeatmapLocalidade";
import ComunicacaoCard from "@/components/ComunicacaoCard";
import AnaliseConversaoCard from "@/components/AnaliseConversaoCard";
import MargemPlanoCard from "@/components/MargemPlanoCard";
import MetasTrimestrais from "@/components/MetasTrimestrais";
import PfPjInsightsCard from "@/components/PfPjInsightsCard";
import MinhaMetaWidget from "@/components/MinhaMetaWidget";
import RankingVendasVendedor from "@/components/RankingVendasVendedor";
import RankingReceitaVendedor from "@/components/RankingReceitaVendedor";
import MetaGlobalCard from "@/components/MetaGlobalCard";
import ComparativoMetasChart from "@/components/ComparativoMetasChart";
import RankingCondominios from "@/components/RankingCondominios";
import RankingConsultores from "@/components/RankingConsultores";
import CondominioPanelDialog from "@/components/CondominioPanelDialog";
import ConsultorPanelDialog from "@/components/ConsultorPanelDialog";
import { useAuth, isAdmin, isVendedora } from "@/lib/auth";
import {
  listCondominios,
  listClientes, createCliente, updateCliente, deleteCliente,
  listMetasVendedores, listMetasMensais, getConfig, listVendedores,
} from "@/lib/api";
import { fmtInt, fmtBRL, exportCSV, formatDocumento, documentoTipo, isValidDocumento } from "@/lib/format";
import { fetchCNPJ } from "@/lib/brasilapi";
import {
  DEFAULT_PLANS, DEFAULT_BONUS_PCT, computeVendorMetrics,
  withBonusAndMeta, filterByMonth, getPlanNames,
} from "@/lib/plans";

const emptyForm = {
  vendedor: "",
  nome_cliente: "",
  endereco: "",
  telefone: "",
  email: "",
  cpf: "",
  plano_escolhido: "",
  app_escolhido: "",
  condominio: "",
};

const CONDO_NONE = "__none__";
const CONDO_FILTER_ALL = "todos";
const CONDO_FILTER_NONE = "__sem__";

const APPS_SUGGESTIONS = ["Netflix", "Amazon Prime Video", "HBO Max", "Disney+", "Paramount+", "Globoplay", "Apple TV+", "YouTube Premium", "Spotify", "Deezer"];

const PLANO_COLOR = {
  "Básico": "bg-slate-500/15 text-slate-400 border-slate-500/30",
  "Intermediário": "bg-blue-500/15 text-blue-400 border-blue-500/30",
  "Premium": "bg-amber-500/15 text-amber-500 border-amber-500/30",
  "Empresarial": "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

const RANK_MEDAL = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15" },
];

export default function ClientesTab() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const vend = isVendedora(user);
  const showProfit = !vend; // vendedora vê só receita bruta, sem lucro/margem
  const simpleView = vend;   // painel enxuto: só o essencial
  const myVendedor = (user?.vendedor_name || "").trim();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vendedorFilter, setVendedorFilter] = useState("todos");
  const [condoFilter, setCondoFilter] = useState(CONDO_FILTER_ALL);
  const [pfPjFilter, setPfPjFilter] = useState("todos"); // todos | pf | pj
  const [monthFilter, setMonthFilter] = useState(""); // "" = todos, ou "YYYY-MM"
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [config, setConfig] = useState({ plans: DEFAULT_PLANS, bonus_pct: DEFAULT_BONUS_PCT });
  const [metasMap, setMetasMap] = useState({});
  const [metasClientesMap, setMetasClientesMap] = useState({});
  const [bonusPctPerVendedor, setBonusPctPerVendedor] = useState({});
  const [metasHistoricas, setMetasHistoricas] = useState({}); // { vendedor: { "YYYY-MM": meta } }
  const [commissionMap, setCommissionMap] = useState({}); // { vendedor_name: 0..1 }
  const minhaMeta = myVendedor ? Number(metasMap[myVendedor] || 0) : 0;
  const minhaMetaClientes = myVendedor ? Number(metasClientesMap[myVendedor] || 0) : 0;
  const minhaComissao = myVendedor ? Number(commissionMap[myVendedor] || 0) : 0;
  const [condoConsultorMap, setCondoConsultorMap] = useState({}); // { consultor: { vendas, meta, condominios } }
  const [condominios, setCondominios] = useState([]); // lista bruta para o Select de vínculo do cliente/prospecto
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjLoadedFor, setCnpjLoadedFor] = useState(""); // último CNPJ já buscado (evita re-fetch)

  const loadClientes = useCallback(async () => {
    setLoading(true);
    try { setItems(await listClientes()); }
    catch (err) {
      console.error("Erro ao carregar clientes:", err);
      toast.error("Erro ao carregar clientes");
    }
    finally { setLoading(false); }
  }, []);

  const loadMetas = useCallback(async () => {
    try {
      const list = await listMetasVendedores();
      const map = {};
      const mapCli = {};
      const mapBonus = {};
      for (const m of list) {
        map[m.vendedor] = Number(m.meta_mensal || 0);
        mapCli[m.vendedor] = Number(m.meta_clientes || 0);
        if (m.bonus_pct !== null && m.bonus_pct !== undefined) {
          mapBonus[m.vendedor] = Number(m.bonus_pct);
        }
      }
      setMetasMap(map);
      setMetasClientesMap(mapCli);
      setBonusPctPerVendedor(mapBonus);
    } catch (err) {
      console.error("Erro ao carregar metas:", err);
    }
  }, []);

  const loadMetasHistoricas = useCallback(async () => {
    try {
      const list = await listMetasMensais();
      const map = {};
      for (const m of list) {
        const v = (m.vendedor || "").trim();
        if (!v) continue;
        if (!map[v]) map[v] = {};
        map[v][m.month] = Number(m.meta_mensal || 0);
      }
      setMetasHistoricas(map);
    } catch (err) {
      console.error("Erro ao carregar histórico de metas:", err);
    }
  }, []);

  const loadCommission = useCallback(async () => {
    try {
      const list = await listVendedores();
      const map = {};
      for (const v of list) {
        if (v.vendedor_name) map[v.vendedor_name] = Number(v.commission_pct || 0);
      }
      setCommissionMap(map);
    } catch (err) {
      console.error("Erro ao carregar comissões (usando fallback):", err);
      // fallback: se a rota falhar, tenta o próprio dado do usuário logado
      if (user?.vendedor_name) {
        setCommissionMap({ [user.vendedor_name]: Number(user.commission_pct || 0) });
      }
    }
  }, [user]);

  const loadConfig = useCallback(async () => {
    try {
      const cfg = await getConfig();
      if (cfg && Array.isArray(cfg.plans) && cfg.plans.length) {
        setConfig({
          plans: cfg.plans,
          bonus_pct: typeof cfg.bonus_pct === "number" ? cfg.bonus_pct : DEFAULT_BONUS_PCT,
        });
      }
    } catch (err) {
      console.error("Erro ao carregar configuração:", err);
    }
  }, []);

  const loadCondoConsultor = useCallback(async () => {
    // Soma vendas_realizadas/meta_vendas por consultor a partir dos condomínios atendidos.
    try {
      const list = await listCondominios();
      setCondominios(list);
      const map = {};
      for (const c of list) {
        if ((c.infra_status || "atendido") !== "atendido") continue;
        const key = (c.consultor || "").trim();
        if (!key) continue;
        const acc = map[key] || { vendas: 0, meta: 0, condominios: 0 };
        acc.vendas += Number(c.vendas_realizadas || 0);
        acc.meta += Number(c.meta_vendas || 0);
        acc.condominios += 1;
        map[key] = acc;
      }
      setCondoConsultorMap(map);
    } catch (err) {
      console.error("Erro ao carregar condomínios (consultor rollup):", err);
    }
  }, []);

  const loadMetasAll = useCallback(async () => {
    await Promise.all([loadMetas(), loadMetasHistoricas()]);
  }, [loadMetas, loadMetasHistoricas]);

  useEffect(() => {
    loadClientes();
    loadMetas();
    loadMetasHistoricas();
    loadConfig();
    loadCommission();
    loadCondoConsultor();
  }, [loadClientes, loadMetas, loadMetasHistoricas, loadConfig, loadCommission, loadCondoConsultor]);

  // Recarrega a lista de clientes quando o vendedor valida um cadastro pendente.
  useEffect(() => {
    const onValidated = () => loadClientes();
    window.addEventListener("onboarding:validated", onValidated);
    return () => window.removeEventListener("onboarding:validated", onValidated);
  }, [loadClientes]);

  // Auto-lookup de CNPJ via BrasilAPI quando o campo cpf tem 14 dígitos válidos.
  useEffect(() => {
    if (!open) return; // só dentro do dialog
    const digits = String(form.cpf || "").replace(/\D/g, "");
    if (digits.length !== 14) return;
    if (!isValidDocumento(form.cpf)) return;
    if (cnpjLoadedFor === digits) return; // já buscado
    let cancelled = false;
    const t = setTimeout(async () => {
      setCnpjLoading(true);
      try {
        const info = await fetchCNPJ(digits);
        if (cancelled) return;
        setCnpjLoadedFor(digits);
        setForm((prev) => {
          const next = { ...prev };
          const nomeAuto = info.nome_fantasia || info.razao_social;
          if (nomeAuto && !prev.nome_cliente.trim()) next.nome_cliente = nomeAuto;
          if (info.endereco_completo && !prev.endereco.trim()) next.endereco = info.endereco_completo;
          if (info.telefone && !prev.telefone.trim()) next.telefone = info.telefone;
          if (info.email && !prev.email.trim()) next.email = info.email;
          return next;
        });
        toast.success(
          info.razao_social
            ? `Dados carregados: ${info.razao_social}${info.situacao ? ` · ${info.situacao}` : ""}`
            : "Dados da empresa carregados"
        );
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Falha ao consultar CNPJ");
      } finally {
        if (!cancelled) setCnpjLoading(false);
      }
    }, 400); // debounce
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [form.cpf, open]);

  // Reset do lookup ao fechar/abrir o dialog
  useEffect(() => { if (!open) setCnpjLoadedFor(""); }, [open]);

  // Set default plano_escolhido from config once loaded
  useEffect(() => {
    if (config.plans.length && !form.plano_escolhido) {
      setForm((prev) => ({ ...prev, plano_escolhido: config.plans[0].name }));
    }
  }, [config, form.plano_escolhido]);

  const availableMonths = useMemo(() => {
    const set = new Set();
    for (const c of items) {
      if (c.created_at) set.add(c.created_at.slice(0, 7)); // YYYY-MM
    }
    return [...set].sort().reverse();
  }, [items]);

  const monthLabelPT = (yyyyMm) => {
    const [y, m] = yyyyMm.split("-");
    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    return `${meses[parseInt(m, 10) - 1] || m}/${y}`;
  };

  const vendedores = useMemo(() => {
    const set = new Set(items.map((i) => (i.vendedor || "").trim()).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const itemsInMonth = useMemo(() => filterByMonth(items, monthFilter), [items, monthFilter]);

  const pfPjCounts = useMemo(() => {
    let pf = 0, pj = 0;
    for (const i of itemsInMonth) {
      const d = String(i.cpf || "").replace(/\D/g, "");
      if (d.length === 14) pj++;
      else pf++; // PF ou sem doc
    }
    return { todos: itemsInMonth.length, pf, pj };
  }, [itemsInMonth]);

  const filtered = useMemo(() => {
    return itemsInMonth.filter((i) => {
      const s = search.toLowerCase();
      const match = !search ||
        (i.nome_cliente || "").toLowerCase().includes(s) ||
        (i.vendedor || "").toLowerCase().includes(s) ||
        (i.cpf || "").toLowerCase().includes(s) ||
        (i.email || "").toLowerCase().includes(s) ||
        (i.condominio || "").toLowerCase().includes(s);
      const mV = vendedorFilter === "todos" || i.vendedor === vendedorFilter;
      const mC =
        condoFilter === CONDO_FILTER_ALL ? true
          : condoFilter === CONDO_FILTER_NONE ? !(i.condominio || "").trim()
          : (i.condominio || "") === condoFilter;
      const digits = String(i.cpf || "").replace(/\D/g, "");
      const isPJ = digits.length === 14;
      const isPF = digits.length === 11;
      const mDoc =
        pfPjFilter === "todos" ||
        (pfPjFilter === "pj" && isPJ) ||
        (pfPjFilter === "pf" && (isPF || !digits));
      return match && mV && mC && mDoc;
    });
  }, [itemsInMonth, search, vendedorFilter, condoFilter, pfPjFilter]);

  const ranking = useMemo(() => {
    const map = new Map();
    for (const c of itemsInMonth) {
      const key = (c.vendedor || "").trim() || "Sem vendedor";
      const acc = map.get(key) || { nome: key, total: 0, planos: {} };
      acc.total += 1;
      acc.planos[c.plano_escolhido || "—"] = (acc.planos[c.plano_escolhido || "—"] || 0) + 1;
      map.set(key, acc);
    }
    const arr = [...map.values()].sort((a, b) => b.total - a.total);
    const max = arr[0]?.total || 1;
    return arr.map((v) => ({ ...v, pct: (v.total / max) * 100 }));
  }, [itemsInMonth]);

  const baseMetrics = useMemo(
    () => computeVendorMetrics(itemsInMonth, config.plans, commissionMap),
    [itemsInMonth, config.plans, commissionMap]
  );
  const receitaTotalPeriodo = useMemo(
    () => baseMetrics.reduce((s, v) => s + v.receita, 0),
    [baseMetrics]
  );
  const teamGoal = Number(config.team_goal || 0);
  const teamHit = teamGoal > 0 && receitaTotalPeriodo >= teamGoal;
  const vendorMetrics = useMemo(
    () => withBonusAndMeta(baseMetrics, metasMap, config.bonus_pct, teamHit, Number(config.team_bonus_pct || 0), bonusPctPerVendedor),
    [baseMetrics, metasMap, config.bonus_pct, teamHit, config.team_bonus_pct, bonusPctPerVendedor]
  );
  const receitaTotal = useMemo(
    () => vendorMetrics.reduce((s, v) => s + v.receita, 0),
    [vendorMetrics]
  );
  const comissaoTotalPaga = useMemo(
    () => vendorMetrics.reduce((s, v) => s + (v.comissao_total || 0), 0),
    [vendorMetrics]
  );
  const lucroBrutoTotal = useMemo(
    () => vendorMetrics.reduce((s, v) => s + (v.lucro_bruto || 0), 0),
    [vendorMetrics]
  );
  const lucroLiquidoFinal = lucroBrutoTotal - comissaoTotalPaga;

  const appMaisVendido = useMemo(() => {
    const counter = {};
    for (const i of itemsInMonth) {
      const key = (i.app_escolhido || "").trim();
      if (!key) continue;
      counter[key] = (counter[key] || 0) + 1;
    }
    const arr = Object.entries(counter).sort((a, b) => b[1] - a[1]);
    return arr[0] || null;
  }, [itemsInMonth]);

  const [prospectosCount, setProspectosCount] = useState(0);
  const [prospectosData, setProspectosData] = useState([]);
  // Painel do condomínio (usado pelo ranking clicável)
  const [panelCondo, setPanelCondo] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const handleOpenCondoPanel = (nome) => {
    setCondoFilter(nome);
    const found = condominios.find((c) => c.condominio === nome);
    setPanelCondo(found || { condominio: nome, tipo: "", infra_status: "atendido", consultor: "", responsavel: "", qtd_apartamentos: 0 });
    setPanelOpen(true);
  };
  // Painel do consultor (aberto a partir do pódio)
  const [consultorPanelOpen, setConsultorPanelOpen] = useState(false);
  const [consultorPanelName, setConsultorPanelName] = useState("");
  const handleOpenConsultor = (nome) => {
    setConsultorPanelName(nome);
    setConsultorPanelOpen(true);
  };
  const [convertingCallback, setConvertingCallback] = useState(null);

  const prospectosPorVendedorNoMes = useMemo(() => {
    const map = {};
    for (const p of prospectosData) {
      const key = (p.data_registro || p.created_at || "").slice(0, 7);
      if (monthFilter && key !== monthFilter) continue;
      const v = (p.vendedor || "").trim() || "Sem vendedor";
      map[v] = (map[v] || 0) + 1;
    }
    return map;
  }, [prospectosData, monthFilter]);

  const vendedoresProsp = useMemo(() => {
    const s = new Set(prospectosData.map((p) => (p.vendedor || "").trim()).filter(Boolean));
    for (const v of vendedores) s.add(v);
    return [...s].sort();
  }, [prospectosData, vendedores]);

  const handleConvertProspecto = (prospecto, onDone) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      vendedor: prospecto.vendedor || "",
      nome_cliente: prospecto.nome || "",
      endereco: prospecto.localidade || "",
      telefone: prospecto.contato || "",
      condominio: prospecto.condominio || "",
      plano_escolhido: config.plans[0]?.name || "",
      app_escolhido: "",
    });
    setConvertingCallback(() => onDone);
    setOpen(true);
    toast.info(`Convertendo "${prospecto.nome}" em cliente — complete os dados que faltam`);
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, plano_escolhido: config.plans[0]?.name || "" });
    setOpen(true);
  };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyForm, ...item }); setOpen(true); };

  const handleSave = async () => {
    if (!form.vendedor.trim() || !form.nome_cliente.trim()) {
      toast.error("Informe o vendedor e o nome do cliente"); return;
    }
    if (form.cpf && !isValidDocumento(form.cpf)) {
      toast.error(`${documentoTipo(form.cpf)} inválido — confira os dígitos verificadores`); return;
    }
    try {
      if (editing) { await updateCliente(editing.id, form); toast.success("Cliente atualizado"); }
      else { await createCliente(form); toast.success("Cliente cadastrado"); }
      if (convertingCallback) {
        try { await convertingCallback(); } catch { /* ignore */ }
        setConvertingCallback(null);
      }
      setOpen(false); loadClientes();
    } catch { toast.error("Erro ao salvar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    try { await deleteCliente(id); toast.success("Cliente excluído"); loadClientes(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const handleExport = () => {
    exportCSV(`clientes${monthFilter ? "-" + monthFilter : ""}.csv`, filtered, [
      { key: "vendedor", label: "Vendedor" },
      { key: "nome_cliente", label: "Nome do Cliente" },
      { key: "condominio", label: "Condomínio" },
      { key: "endereco", label: "Endereço" },
      { key: "telefone", label: "Telefone" },
      { key: "email", label: "E-mail" },
      { key: "cpf", label: "CPF / CNPJ" },
      { key: "plano_escolhido", label: "Plano" },
      { key: "app_escolhido", label: "App" },
    ]);
    toast.success("CSV exportado");
  };

  return (
    <div className="space-y-6">
      {/* Link público de auto-cadastro + fila de aprovação (destaque no topo) */}
      <div className="rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-1 shadow-xl shadow-primary/10" data-testid="onboarding-highlight">
        <div className="rounded-xl bg-background/60 backdrop-blur-sm p-4 space-y-4">
          <OnboardingLinkCard />
          <OnboardingPendentesCard />
        </div>
      </div>
      {/* Month filter */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange className="w-4 h-4 text-primary" />
          <span className="font-medium">Período</span>
        </div>
        <Select value={monthFilter || "all"} onValueChange={(v) => setMonthFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="filter-month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os meses</SelectItem>
            {availableMonths.map((m) => (
              <SelectItem key={m} value={m}>{monthLabelPT(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">
          {monthFilter
            ? `Mostrando ${itemsInMonth.length} venda(s) de ${monthLabelPT(monthFilter)}`
            : `Mostrando ${itemsInMonth.length} venda(s) no total`}
        </div>
      </div>

      {/* Widget de meta individual (só para vendedora) */}
      {vend && (
        <>
          <MetaGlobalCard editable={false} teamReceita={receitaTotal} teamClientes={itemsInMonth.length} config={config} />
          <MinhaMetaWidget
            vendedorName={myVendedor}
            clientes={itemsInMonth}
            allClientes={items}
            todosClientes={items}
            vendedoresAtivos={vendedores}
            plans={config.plans}
            meta={minhaMeta}
            metasHistoricas={metasHistoricas[myVendedor] || {}}
            commissionPct={minhaComissao}
            bonusPct={Number(config.bonus_pct || 0)}
            monthLabel={monthFilter ? monthLabelPT(monthFilter) : ""}
            rank={(() => {
              const sorted = [...vendorMetrics].sort((a, b) => (b.receita || 0) - (a.receita || 0));
              const idx = sorted.findIndex((m) => m.nome === myVendedor);
              return idx >= 0 ? idx + 1 : null;
            })()}
            totalVend={vendorMetrics.length}
          />
          {/* Widget da meta em CLIENTES (qtd) — complementar ao financeiro */}
          {minhaMetaClientes > 0 && (() => {
            const meusClientes = itemsInMonth.filter((c) => (c.vendedor || "") === myVendedor).length;
            const p = Math.min(100, Math.round((meusClientes / Math.max(1, minhaMetaClientes)) * 100));
            const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
            return (
              <div className="rounded-xl border border-border bg-card p-5 card-flua" data-testid="minha-meta-clientes">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <h3 className="font-heading font-semibold text-lg">Minha meta em clientes</h3>
                  </div>
                  <div className={`font-heading text-2xl font-extrabold tabular-nums ${tone}`}>{p}%</div>
                </div>
                <Progress value={p} className="h-2 mb-2" />
                <div className="text-xs text-muted-foreground tabular-nums">
                  <span className="text-foreground font-mono">{meusClientes}</span> de <span className="text-foreground font-mono">{minhaMetaClientes}</span> clientes fechados este mês
                </div>
              </div>
            );
          })()}
          {/* Top 3 por qtd de clientes + Top 3 por receita */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RankingVendasVendedor />
            <RankingReceitaVendedor />
          </div>
        </>
      )}

      {admin && (() => {
        const semMeta = vendedores.filter((v) => !metasMap[v] || Number(metasMap[v]) <= 0);
        if (semMeta.length === 0) return null;
        return (
          <div className="rounded-xl border-2 border-amber-500/50 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="alert-sem-meta">
            <div className="w-10 h-10 rounded-lg grid place-items-center bg-amber-500/20 border border-amber-500/40 shrink-0">
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-heading font-bold text-amber-500">
                  {semMeta.length === 1 ? "1 vendedora sem meta configurada" : `${semMeta.length} vendedoras sem meta configurada`}
                </h4>
                <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[10px]">Configure para pagar bônus</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Sem meta mensal, o bônus não é calculado nem o gráfico histórico compara desempenho. Defina a meta no card "Comissões" abaixo.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {semMeta.map((n) => (
                  <Badge key={n} variant="outline" className="border-amber-500/40 text-amber-400" data-testid={`sem-meta-${n}`}>
                    <Users className="w-3 h-3 mr-1" /> {n}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="kpi-total-clientes-fechados" label="Clientes fechados" value={fmtInt(itemsInMonth.length)} icon={Users} />
        <StatCard testId="kpi-receita-bruta" label="Receita bruta" value={fmtBRL(receitaTotal)} icon={DollarSign} tone="success" />
        {showProfit && (
          <StatCard testId="kpi-lucro-bruto" label="Lucro (sem comissão)"
            value={fmtBRL(lucroBrutoTotal)}
            hint="Soma do lucro líquido dos planos vendidos"
            icon={Sparkles}
            tone="success" />
        )}
        {showProfit && (
          <StatCard testId="kpi-lucro-liquido-final" label="Lucro (com comissão)"
            value={fmtBRL(lucroLiquidoFinal)}
            hint={`Comissões pagas: ${fmtBRL(comissaoTotalPaga)}`}
            icon={TrendingDown}
            tone={lucroLiquidoFinal > 0 ? "success" : "danger"} />
        )}
        <StatCard testId="kpi-plano-mais-vendido" label="Plano mais vendido"
          value={(() => {
            const counter = {};
            for (const i of itemsInMonth) counter[i.plano_escolhido || "—"] = (counter[i.plano_escolhido || "—"] || 0) + 1;
            const arr = Object.entries(counter).sort((a, b) => b[1] - a[1]);
            return arr[0] ? arr[0][0] : "—";
          })()}
          hint={(() => {
            const counter = {};
            for (const i of itemsInMonth) counter[i.plano_escolhido || "—"] = (counter[i.plano_escolhido || "—"] || 0) + 1;
            const arr = Object.entries(counter).sort((a, b) => b[1] - a[1]);
            return arr[0] ? `${arr[0][1]} venda(s)` : "";
          })()}
          icon={Award} />
        <StatCard testId="kpi-app-mais-vendido" label="App mais vendido"
          value={appMaisVendido ? appMaisVendido[0] : "—"}
          hint={appMaisVendido ? `${appMaisVendido[1]} venda(s)` : "Cadastre o app do cliente"}
          icon={Smartphone} tone="default" />
        <StatCard testId="kpi-prospectos-total" label="Prospectos" value={fmtInt(prospectosCount)}
          hint="Leads futuros / sem viabilidade agora" icon={Sparkles} tone="warning" />
      </div>

      {/* Toolbar */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search-clientes"
            placeholder="Buscar por cliente, vendedor, CPF/CNPJ ou e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={vendedorFilter} onValueChange={setVendedorFilter}>
          <SelectTrigger className="w-full lg:w-[220px]" data-testid="filter-vendedor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os vendedores</SelectItem>
            {vendedores.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={condoFilter} onValueChange={setCondoFilter}>
          <SelectTrigger className="w-full lg:w-[220px]" data-testid="filter-condominio-cliente">
            <div className="flex items-center gap-2 truncate">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <SelectValue placeholder="Todos os condomínios" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CONDO_FILTER_ALL}>Todos os condomínios</SelectItem>
            <SelectItem value={CONDO_FILTER_NONE}>Sem vínculo</SelectItem>
            {condominios.map((c) => (
              <SelectItem key={c.id} value={c.condominio} data-testid={`filter-condo-opt-${c.id}`}>
                {c.condominio}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-md border border-border overflow-hidden shrink-0" data-testid="filter-pf-pj">
          {[
            { key: "todos", label: "Todos", count: pfPjCounts.todos },
            { key: "pf", label: "PF", count: pfPjCounts.pf, tone: "text-emerald-500" },
            { key: "pj", label: "PJ", count: pfPjCounts.pj, tone: "text-sky-500" },
          ].map((b) => {
            const active = pfPjFilter === b.key;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => setPfPjFilter(b.key)}
                data-testid={`filter-pf-pj-${b.key}`}
                className={`px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  active ? "bg-primary text-primary-foreground" : `bg-card hover:bg-muted ${b.tone || ""}`
                }`}
              >
                {b.label}
                <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "opacity-60"}`}>
                  {b.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="btn-export-csv-clientes">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
          <Button onClick={openCreate} data-testid="btn-add-cliente">
            <Plus className="w-4 h-4 mr-2" /> Novo cliente
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="thin-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Vendedor</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Condomínio</TableHead>
                <TableHead>Endereço</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>CPF / CNPJ</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>App</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Carregando…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Nenhum cliente encontrado no período.</TableCell></TableRow>
              )}
              {filtered.map((i) => (
                <TableRow key={i.id} data-testid={`cliente-row-${i.id}`}>
                  <TableCell className="font-medium">{i.vendedor}</TableCell>
                  <TableCell>{i.nome_cliente}
                    {(() => {
                      const digits = String(i.cpf || "").replace(/\D/g, "");
                      if (digits.length !== 11 && digits.length !== 14) return null;
                      const isPJ = digits.length === 14;
                      return (
                        <Badge
                          variant="outline"
                          className={`ml-2 text-[10px] py-0 h-4 ${
                            isPJ ? "text-sky-500 border-sky-500/40" : "text-emerald-500 border-emerald-500/40"
                          }`}
                        >
                          {isPJ ? "PJ" : "PF"}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {i.condominio ? (
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 gap-1" data-testid={`cliente-condo-${i.id}`}>
                        <Building2 className="w-2.5 h-2.5" /> <span className="max-w-[140px] truncate">{i.condominio}</span>
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground" title={i.endereco}>{i.endereco || "—"}</TableCell>
                  <TableCell className="tabular-nums text-sm">{i.telefone || "—"}</TableCell>
                  <TableCell className="text-sm">{i.email || "—"}</TableCell>
                  <TableCell className="tabular-nums text-sm">{i.cpf || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={PLANO_COLOR[i.plano_escolhido] || ""}>
                      {i.plano_escolhido || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{i.app_escolhido || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`btn-edit-cliente-${i.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(i.id)} data-testid={`btn-delete-cliente-${i.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Ranking por número de vendas */}
      {!simpleView && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-vendedores-clientes">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold text-lg">Ranking por vendas</h3>
            </div>
            <Badge variant="outline">Contagem de contratos fechados</Badge>
          </div>
          {ranking.length === 0 && <div className="text-sm text-muted-foreground">Sem vendas no período.</div>}
          <div className="space-y-3">
            {ranking.map((v, idx) => {
              const medal = RANK_MEDAL[idx];
              const Icon = medal?.icon;
              return (
                <div
                  key={v.nome}
                  data-testid={`rank-vendedor-${idx + 1}`}
                  className={`rounded-lg border p-4 flex items-center gap-4 ${idx === 0 ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${medal?.bg || "bg-muted"}`}>
                    {Icon ? <Icon className={`w-5 h-5 ${medal.color}`} /> : <span className="font-bold text-muted-foreground">{idx + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-heading font-semibold truncate">{v.nome}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {Object.entries(v.planos).map(([p, n]) => `${p}: ${n}`).join(" · ")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-2xl font-extrabold tabular-nums text-primary">{fmtInt(v.total)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">vendas</div>
                      </div>
                    </div>
                    <Progress value={v.pct} className="h-2 mt-3" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!simpleView && <PfPjInsightsCard clientes={itemsInMonth} plans={config.plans} />}
      {!simpleView && <ComparativoMensal clientes={items} plans={config.plans} months={6} />}
      {!simpleView && <MetasTrimestrais clientes={items} plans={config.plans} commissionMap={commissionMap} />}
      {showProfit && <MargemPlanoCard clientes={itemsInMonth} plans={config.plans} />}
      <RankingApps clientes={itemsInMonth} />
      <ProspectosCard
        onCountChange={setProspectosCount}
        onDataChange={setProspectosData}
        onConvertToCliente={handleConvertProspecto}
        config={config}
        condominios={condominios}
      />
      {!simpleView && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <MotivosFrequentesCard prospectos={prospectosData} />
          <HeatmapLocalidade prospectos={prospectosData} />
        </div>
      )}
      {!simpleView && <AnaliseConversaoCard prospectos={prospectosData} />}
      <TempoRespostaOnboardingCard />}
      {!simpleView && <MetasProspeccao prospectosPorVendedor={prospectosPorVendedorNoMes} vendedores={vendedoresProsp} />}
      {!simpleView && <TeamGoalCard receitaAtual={receitaTotalPeriodo} config={config} onChange={setConfig} readOnly={!admin} />}
      {!simpleView && <RankingReceita metrics={vendorMetrics} />}
      {!simpleView && admin && (
        <MetaGlobalCard
          editable={true}
          teamReceita={receitaTotal}
          teamClientes={itemsInMonth.length}
          config={config}
          onConfigChange={setConfig}
        />
      )}
      {!simpleView && admin && (
        <ComparativoMetasChart
          metrics={vendorMetrics}
          metasMap={metasMap}
          metasClientesMap={metasClientesMap}
        />
      )}
      {!simpleView && <MetasVendedor metrics={vendorMetrics} bonusPct={config.bonus_pct} />}
      <ComissoesCard metrics={vendorMetrics} plans={config.plans} bonusPct={config.bonus_pct} teamBonusPct={config.team_bonus_pct} teamHit={teamHit} month={monthFilter} commissionMap={commissionMap} condoConsultorMap={condoConsultorMap} metasMap={metasMap} onMetaSaved={loadMetasAll} />
      <RankingCondominios
        clientes={itemsInMonth}
        monthLabel={monthFilter ? monthLabelPT(monthFilter) : ""}
        onSelect={handleOpenCondoPanel}
      />
      {!simpleView && (
        <RankingConsultores
          condoConsultorMap={condoConsultorMap}
          monthLabel={monthFilter ? monthLabelPT(monthFilter) : ""}
          onSelect={handleOpenConsultor}
        />
      )}
      {admin && <ComunicacaoCard config={config} onChange={setConfig} />}

      <CondominioPanelDialog
        open={panelOpen}
        onOpenChange={setPanelOpen}
        condominio={panelCondo}
        clientes={items}
        prospectos={prospectosData}
      />
      <ConsultorPanelDialog
        open={consultorPanelOpen}
        onOpenChange={setConsultorPanelOpen}
        consultor={consultorPanelName}
        condominios={condominios}
        clientes={items}
      />

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cliente">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Vendedor *</Label>
              <Input data-testid="input-cliente-vendedor" value={form.vendedor} onChange={(e) => setF("vendedor", e.target.value)} placeholder="Ex: Ana Souza" />
            </div>
            <div className="space-y-1.5">
              <Label>Nome do cliente *</Label>
              <Input data-testid="input-cliente-nome" value={form.nome_cliente} onChange={(e) => setF("nome_cliente", e.target.value)} placeholder="Ex: João da Silva" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Endereço</Label>
              <Input value={form.endereco} onChange={(e) => setF("endereco", e.target.value)} placeholder="Rua, número, complemento" />
            </div>
            <div className="space-y-1.5">
              <Label>Telefone</Label>
              <Input value={form.telefone} onChange={(e) => setF("telefone", e.target.value)} placeholder="5511999998888" />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="cliente@email.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>CPF / CNPJ</Label>
                <div className="flex items-center gap-1.5">
                  {cnpjLoading && (
                    <span className="flex items-center gap-1 text-[10px] text-sky-500" data-testid="cnpj-lookup-loading">
                      <Loader2 className="w-3 h-3 animate-spin" /> BrasilAPI…
                    </span>
                  )}
                  {form.cpf && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] py-0 h-5 tabular-nums ${
                        isValidDocumento(form.cpf) ? "text-emerald-500 border-emerald-500/40" : "text-red-500 border-red-500/40"
                      }`}
                    >
                      {documentoTipo(form.cpf)} {isValidDocumento(form.cpf) ? "válido" : "inválido"}
                    </Badge>
                  )}
                </div>
              </div>
              <Input
                value={form.cpf}
                onChange={(e) => setF("cpf", formatDocumento(e.target.value))}
                placeholder="CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00)"
                data-testid="input-cpf-cnpj"
                inputMode="numeric"
                maxLength={18}
                className={form.cpf && !isValidDocumento(form.cpf) ? "border-red-500/50 focus-visible:ring-red-500/40" : ""}
              />
              {form.cpf && !isValidDocumento(form.cpf) && (
                <p className="text-[11px] text-red-500" data-testid="cpf-cnpj-error">
                  Dígitos verificadores inválidos — confira o {documentoTipo(form.cpf)}.
                </p>
              )}
              {isValidDocumento(form.cpf) && documentoTipo(form.cpf) === "CNPJ" && !cnpjLoading && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {cnpjLoadedFor === String(form.cpf).replace(/\D/g, "")
                    ? "Dados carregados da BrasilAPI (só preenche campos vazios)"
                    : "Buscando dados da Receita via BrasilAPI…"}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Plano escolhido</Label>
              <Select value={form.plano_escolhido} onValueChange={(v) => setF("plano_escolhido", v)}>
                <SelectTrigger data-testid="select-plano"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {getPlanNames(config.plans).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>App escolhido</Label>
              <Input
                value={form.app_escolhido}
                onChange={(e) => setF("app_escolhido", e.target.value)}
                placeholder="Ex: Netflix"
                list="apps-suggestions"
                data-testid="input-app-escolhido"
              />
              <datalist id="apps-suggestions">
                {APPS_SUGGESTIONS.map((a) => <option key={a} value={a} />)}
              </datalist>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                Condomínio (opcional)
              </Label>
              <Select
                value={form.condominio || CONDO_NONE}
                onValueChange={(v) => setF("condominio", v === CONDO_NONE ? "" : v)}
              >
                <SelectTrigger data-testid="select-cliente-condominio">
                  <SelectValue placeholder="Sem vínculo com condomínio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CONDO_NONE}>Sem vínculo</SelectItem>
                  {condominios.map((c) => (
                    <SelectItem key={c.id} value={c.condominio} data-testid={`opt-cliente-condo-${c.id}`}>
                      {c.condominio}
                      <span className="text-[10px] text-muted-foreground ml-2">
                        {(c.infra_status || "atendido") === "atendido" ? "atendido" : "prospecto"}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="btn-save-cliente">{editing ? "Salvar alterações" : "Cadastrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
